import React, { useState, useEffect } from 'react';
import {
  Search,
  ShoppingCart,
  User,
  LogOut,
  Truck,
  Phone,
  Mail,
  FileText,
  HelpCircle,
  Menu,
  X,
  ChevronRight,
  LayoutGrid 
} from 'lucide-react';
import SignupModal from './SignupModal';
import { useUser } from '../contexts/UserContext'; 
import { useCart } from './CartContext';
import { Link, useNavigate } from 'react-router-dom';
import './Header.css';
import logo from '../assets/TradyFroglogo.png';

const TOP_BAR_HEIGHT_PX = 40; 

const Header: React.FC = () => {
  const [isUserMenuVisible, setIsUserMenuVisible] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [initialUserType, setInitialUserType] = useState<'buyer' | 'seller'>('buyer');

  const [showTopBar, setShowTopBar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const { user, logout } = useUser();
  const { cartItemCount } = useCart();
  const navigate = useNavigate();

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    logout();
    setIsUserMenuVisible(false);
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  const controlDirection = () => {
    if (typeof window !== 'undefined') {
      if (window.scrollY > 50) {
        setShowTopBar(false);
      } else {
        setShowTopBar(true);
      }
      setLastScrollY(window.scrollY);
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', controlDirection);
      return () => {
        window.removeEventListener('scroll', controlDirection);
      };
    }
  }, [lastScrollY]);

  const handleMouseEnter = () => setIsUserMenuVisible(true);
  const handleMouseLeave = () => setIsUserMenuVisible(false);

  return (
    <>
      <header 
        className="fixed top-0 left-0 w-full z-[999] bg-white shadow-md transition-all duration-300"
      >
        
        {/* --- TOP BAR --- */}
        <div className={`bg-[#0f172a] text-white text-xs sm:text-sm hidden md:flex items-center justify-center transition-all duration-300 overflow-hidden`}
             style={{ height: showTopBar ? `${TOP_BAR_HEIGHT_PX}px` : '0px' }}>
          
          <div className="container mx-auto px-4 flex justify-between items-center w-full">
            <div className="flex items-center space-x-4 lg:space-x-6 font-semibold">
              <div className="flex items-center">
                <Phone className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-red-400" />
                <span>Call: <span className="font-bold">+91 9412220200</span></span>
              </div>
              <div className="flex items-center font-bold">
                <Mail className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-red-400" />
                <a href="mailto:info@tradyfrog.com" className="hover:text-red-400">info@tradyfrog.com</a>
              </div>
            </div>

            {/* <div className="flex items-center space-x-4 font-semibold">
              <Link to="/write-review" className="flex items-center hover:text-red-400">
                 <FileText className="w-4 h-4 mr-1 text-blue-300" /> Write Review
              </Link>
              <a href="/login" className="flex items-center hover:text-red-400">
                <Truck className="w-4 h-4 mr-1 text-red-400" /> Track Order
              </a>
              <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer" className="flex items-center hover:text-green-500">
                <HelpCircle className="w-4 h-4 mr-1 text-green-500" /> Support
              </a>
            </div> */}
          </div>
        </div>

        {/* --- MAIN HEADER --- */}
        <div className="bg-white relative z-50">
          <div className="container mx-auto px-4 py-2 sm:py-0 h-auto md:h-20 flex items-center justify-between">
            
            {/* LOGO */}
            <a href="/" className="flex-shrink-0 mr-4">
              <img
                src={logo}
                alt="TradyFrog Logo"
                className="h-12 w-auto md:h-24 lg:h-28 object-contain transition-all duration-300 drop-shadow-md"
              />
            </a>

            {/* SEARCH BAR (Desktop) */}
            <div className="hidden md:flex flex-grow max-w-2xl mx-4">
              <div className="w-full flex items-stretch border-2 border-[#0f6f5c] rounded-md overflow-hidden shadow-sm h-11">
                <input
                  type="text"
                  placeholder="Search Products by title, sku, category..."
                  className="w-full pl-4 pr-3 text-sm lg:text-base focus:outline-none h-full"
                />
                <button className="bg-[#0f6f5c] text-white px-6 hover:bg-[#1b8f78] transition-colors flex items-center justify-center h-full">
                  <Search className="w-5 h-5 md:w-6 md:h-6" />
                </button>
              </div>
            </div>

            {/* NAVIGATION (Visible on MD i.e. Tablets & Desktop) */}
            <nav className="hidden md:flex items-center space-x-2 lg:space-x-4 select-none ">
              
              {/* --- ADDED CATEGORIES FOR IPAD/TABLET/DESKTOP --- */}
              <Link to="/all-categories" className="flex flex-col items-center text-gray-700 hover:text-[#0f6f5c] p-2 transition">
                <LayoutGrid className="w-5 h-5 mb-1" />
                <span className="text-xs font-bold">Categories</span>
              </Link>

              {/* <a href="/quotes" className="flex flex-col items-center text-gray-700 hover:text-[#0f6f5c] p-2 transition">
                <FileText className="w-5 h-5 mb-1" />
                <span className="text-xs font-bold">Quotes</span>
              </a> */}

              <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
               <Link 
                  to="/login" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className=" flex items-center gap-2 text-gray-700 hover:text-[#0f6f5c] p-2 transition bg-gray-100 rounded-full"
                >
                  <div className="bg-gray-100 p-1.5 rounded-full">
                    <User className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="text-left hidden lg:block">
                    <p className="text-[10px] text-gray-500 font-medium">Hello, {user ? user.username?.split(' ')[0] : 'Sign In'}</p>
                    <p className="text-xs font-bold text-gray-800">Account</p>
                  </div>
                </Link>

                {isUserMenuVisible && user && (
                  <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-100 rounded-lg shadow-xl py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-100 mb-2">
                       <p className="font-bold text-gray-800">{user.username}</p>
                       <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                    {[
                      { l: 'My Profile', href: '/buyer/profile?section=profile' },
                      { l: 'My Orders', href: '/buyer/profile?section=orders' },
                      { l: 'My Wishlist', href: '/buyer/profile?section=wishlist' },
                    ].map(item => (
                      <a key={item.l} href={item.href} className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-[#0f6f5c]">
                        {item.l}
                      </a>
                    ))}
                    <div className="border-t border-gray-100 mt-2 pt-2">
                      <button onClick={handleLogout} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center">
                        <LogOut className="w-4 h-4 mr-2" /> Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* <a href="/cart" className="relative group p-2">
                <ShoppingCart className="w-6 h-6 text-gray-700 group-hover:text-[#0f6f5c] transition" />
                {cartItemCount > 0 && (
                  <span className="absolute top-0 right-0 bg-red-600 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                    {cartItemCount}
                  </span>
                )}
                <span className="text-xs font-bold text-gray-700 block text-center mt-1">Cart</span>
              </a> */}
            </nav>

            {/* MOBILE CONTROLS */}
            <div className="flex md:hidden items-center gap-3">
              <Link to="/all-categories" className="p-1">
                <LayoutGrid className="w-6 h-6 text-gray-800" />
              </Link>
              
              {/* <a href="/cart" className="relative p-1">
                <ShoppingCart className="w-6 h-6 text-gray-800" />
                {cartItemCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                    {cartItemCount}
                  </span>
                )}
              </a> */}
              <button onClick={() => setIsMobileMenuOpen(true)} className="p-1">
                <Menu className="w-7 h-7 text-gray-800" />
              </button>
            </div>
          </div>

          {/* SEARCH BAR (Mobile) */}
          <div className="md:hidden px-4 pb-3">
            <div className="flex items-stretch border border-[#0f6f5c] rounded-md overflow-hidden shadow-sm bg-gray-50 h-10">
              <input
                type="text"
                placeholder="Search..."
                className="w-full pl-3 pr-2 text-sm focus:outline-none bg-transparent h-full"
              />
              <button className="bg-[#0f6f5c] text-white px-4 flex items-center justify-center h-full">
                <Search className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* --- SIDEBAR & MODAL COMPONENTS --- */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[10000]" onClick={() => setIsMobileMenuOpen(false)} />
      )}
      
      <div className={`fixed top-0 right-0 h-full w-[80%] max-w-sm bg-white z-[10001] shadow-2xl transform transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="bg-[#0f6f5c] p-4 text-white flex justify-between items-center">
           <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-full"><User className="w-6 h-6 text-white" /></div>
              <div>
                <p className="font-bold text-lg">{user ? user.username : 'Guest'}</p>
                {!user && (
                  /* MODIFIED: Opens /login in new tab instead of opening modal */
                  <Link 
                    to="/login" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-xs underline text-green-100 hover:text-white"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Login / Sign Up
                  </Link>
                )}
              </div>
           </div>
           <button onClick={() => setIsMobileMenuOpen(false)}><X className="w-6 h-6 text-white" /></button>
        </div>
        <div className="p-4 overflow-y-auto h-[calc(100vh-80px)]">
           <div className="space-y-1">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Menu</p>
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 text-gray-700">
                <span className="flex items-center gap-3"><Truck className="w-5 h-5 text-gray-500"/> Track Order</span>
                <ChevronRight className="w-4 h-4 text-gray-400"/>
              </Link>
              <Link to="/write-review" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 text-gray-700">
                <span className="flex items-center gap-3"><FileText className="w-5 h-5 text-gray-500"/> Write Review</span>
                <ChevronRight className="w-4 h-4 text-gray-400"/>
              </Link>
              <hr className="my-2 border-gray-100" />
              {user ? (
                <>
                  <Link to="/buyer/profile" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 text-gray-700">My Profile</Link>
                  <Link to="/buyer/orders" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 text-gray-700">My Orders</Link>
                  <button onClick={handleLogout} className="w-full text-left p-3 rounded-lg hover:bg-red-50 text-red-600 font-semibold flex items-center gap-3 mt-2"><LogOut className="w-5 h-5"/> Logout</button>
                </>
              ) : (
                /* MODIFIED: Login Button now opens in new tab */
                <Link 
                  to="/login" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  onClick={() => setIsMobileMenuOpen(false)} 
                  className="block w-full text-center bg-[#0f6f5c] text-white py-3 rounded-lg font-bold"
                >
                  Login / Register
                </Link>
              )}
           </div>
        </div>
      </div>

      <SignupModal isOpen={isSignupModalOpen} onClose={() => setIsSignupModalOpen(false)} initialView="login" initialUserType={initialUserType} />
    </>
  );
};

export default Header;